import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Sparkles, ShoppingBag, Loader2, Plus, Minus, Search, AlertCircle } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { validateShippingForm, normalizeShippingForm } from "@/lib/validation";

const PERKS = [
  { title: "Wholesale pricing", body: "6, 12 & 24-piece tier rates unlocked across the catalog." },
  { title: "Bulk discounts", body: "Best-in-industry margins on every Blisson SKU you stock." },
  { title: "Salon-only offers", body: "Early access to launches, seasonal promos and free testers." },
  { title: "Concierge support", body: "Dedicated WhatsApp line for re-orders, training & queries." },
];

export default function SalonPartner() {
  const { user, salon, refreshSalon, loginWithGoogle, loading } = useAuth();
  const [form, setForm] = useState({
    salon_name: "",
    owner_name: "",
    phone: "",
    city: "",
    gst_number: "",
    instagram: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && salon?.status === "rejected") {
      // Allow resubmission — prefill existing fields if present
      setForm({
        salon_name: salon.salon_name || "",
        owner_name: salon.owner_name || "",
        phone: salon.phone || "",
        city: salon.city || "",
        gst_number: salon.gst_number || "",
        instagram: salon.instagram || "",
      });
    }
  }, [user, salon]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.salon_name || !form.owner_name || !form.phone || !form.city) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/salon/register", form);
      toast.success("Application submitted! We'll review within 24 hrs.");
      await refreshSalon();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen bg-white" data-testid="salon-partner-page">
      <div className="max-w-[1240px] mx-auto px-6 md:px-12">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <div className="overline mb-4 text-[#C9A961]">Salon Partner Program · Invite Only</div>
          <h1 className="font-serif-display text-5xl md:text-7xl tracking-tighter font-light leading-[1.02]">
            Built for the <em className="gold-text not-italic">professional.</em>
          </h1>
          <p className="text-base md:text-lg text-[#5C5C5C] mt-6 max-w-xl mx-auto leading-relaxed">
            Unlock wholesale-grade pricing, salon-only bundles and concierge re-orders — reserved
            for verified Blisson stockists.
          </p>
        </motion.div>

        {/* Perks strip */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-20" data-testid="salon-perks">
          {PERKS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="border border-black/10 p-6 bg-[#F8F5F0]/40"
            >
              <Sparkles size={18} className="text-[#C9A961] mb-3" strokeWidth={1.3} />
              <div className="font-serif-display text-xl tracking-tight mb-2">{p.title}</div>
              <div className="text-xs text-[#5C5C5C] leading-relaxed">{p.body}</div>
            </motion.div>
          ))}
        </div>

        {/* State-based content */}
        <div className="max-w-2xl mx-auto" data-testid="salon-content">
          {loading ? (
            <div className="text-center overline py-20">Checking access…</div>
          ) : !user ? (
            <NotLoggedIn onLogin={loginWithGoogle} />
          ) : salon?.status === "approved" ? (
            <Approved salon={salon} user={user} />
          ) : salon?.status === "pending" ? (
            <Pending salon={salon} />
          ) : salon?.status === "rejected" ? (
            <Rejected salon={salon}>
              <ApplicationForm form={form} update={update} submit={submit} submitting={submitting} resubmit />
            </Rejected>
          ) : (
            <ApplicationForm form={form} update={update} submit={submit} submitting={submitting} />
          )}
        </div>
      </div>
    </div>
  );
}

function NotLoggedIn({ onLogin }) {
  return (
    <div className="border border-black/10 p-10 text-center" data-testid="salon-need-login">
      <div className="overline mb-3">Step 01</div>
      <h2 className="font-serif-display text-3xl tracking-tight mb-3">Sign in to apply</h2>
      <p className="text-sm text-[#5C5C5C] mb-7 max-w-md mx-auto">
        We use your Google account so you can return any time to track your application status and
        place wholesale orders once approved.
      </p>
      <button onClick={onLogin} className="btn-primary mx-auto" data-testid="salon-login-cta">
        Continue with Google
      </button>
    </div>
  );
}

function ApplicationForm({ form, update, submit, submitting, resubmit = false }) {
  return (
    <form onSubmit={submit} className="border border-black/10 p-8" data-testid="salon-application-form">
      <div className="overline mb-3">{resubmit ? "Re-submit your application" : "Salon Partner Application"}</div>
      <h2 className="font-serif-display text-3xl tracking-tight mb-2">Tell us about your salon.</h2>
      <p className="text-xs text-[#5C5C5C] mb-8">
        We review every application personally within 24 hours.
      </p>

      <div className="space-y-5">
        <FormField label="Salon / Studio Name *" value={form.salon_name} onChange={(v) => update("salon_name", v)} testid="salon-name" />
        <div className="grid sm:grid-cols-2 gap-5">
          <FormField label="Owner Name *" value={form.owner_name} onChange={(v) => update("owner_name", v)} testid="owner-name" />
          <FormField label="Phone (WhatsApp) *" value={form.phone} onChange={(v) => update("phone", v)} testid="phone" />
        </div>
        <FormField label="City *" value={form.city} onChange={(v) => update("city", v)} testid="city" />
        <div className="grid sm:grid-cols-2 gap-5">
          <FormField label="GST Number (optional)" value={form.gst_number} onChange={(v) => update("gst_number", v)} testid="gst" />
          <FormField label="Instagram Handle (optional)" value={form.instagram} onChange={(v) => update("instagram", v)} testid="instagram" placeholder="@yoursalon" />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary w-full mt-8 disabled:opacity-50"
        data-testid="salon-submit"
      >
        {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
        {submitting ? "Submitting…" : resubmit ? "Re-submit Application" : "Submit Application"}
      </button>

      <p className="text-[11px] text-[#5C5C5C] text-center mt-5">
        By submitting you agree to receive review updates on WhatsApp at the number above.
      </p>
    </form>
  );
}

function Pending({ salon }) {
  return (
    <div className="border border-[#C9A961]/40 bg-[#F8F5F0] p-10 text-center" data-testid="salon-pending">
      <Clock size={32} className="text-[#C9A961] mx-auto mb-4" strokeWidth={1.2} />
      <div className="overline mb-2 text-[#C9A961]">Under Review</div>
      <h2 className="font-serif-display text-3xl tracking-tight mb-3">Application received.</h2>
      <p className="text-sm text-[#5C5C5C] max-w-md mx-auto mb-6">
        Thank you, <strong>{salon?.owner_name}</strong>. Our partnerships team is reviewing
        <strong> {salon?.salon_name}</strong>. You'll get a WhatsApp message within 24 hours.
      </p>
      <Link to="/shop" className="btn-outline inline-flex" data-testid="salon-pending-shop-link">
        Continue browsing →
      </Link>
    </div>
  );
}

function Approved({ salon, user }) {
  const [slab, setSlab] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/salon/my-slab").catch(() => ({ data: { has_slab: false } })),
      api.get("/salon/my-stats").catch(() => ({ data: null })),
    ]).then(([s, st]) => {
      setSlab(s.data);
      setStats(st.data);
    }).finally(() => setLoading(false));
  }, []);

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  return (
    <div className="space-y-6" data-testid="salon-approved">
      <div className="border border-[#C9A961] bg-gradient-to-br from-[#F8F5F0] to-white p-10 text-center">
        <CheckCircle2 size={32} className="text-[#C9A961] mx-auto mb-4" strokeWidth={1.2} />
        <div className="overline mb-2 text-[#C9A961]">Wholesale Access Unlocked</div>
        <h2 className="font-serif-display text-3xl tracking-tight mb-3">Welcome, partner.</h2>
        <p className="text-sm text-[#5C5C5C] max-w-md mx-auto mb-7">
          <strong>{salon?.salon_name}</strong> is approved. Wholesale tier pricing is now live across
          every product page — and applied automatically at checkout.
        </p>
        <Link to="/shop" className="btn-primary inline-flex" data-testid="salon-approved-shop-link">
          <ShoppingBag size={14} /> Shop Wholesale Catalog
        </Link>
      </div>

      {/* Lifetime stats */}
      {!loading && stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="lifetime-stats">
          <StatCard label="Total Paid" value={fmt(stats.lifetime_payable)} sub={`${stats.slabs_taken || 0} slabs`} />
          <StatCard label="MRP Unlocked" value={fmt(stats.lifetime_mrp_access)} sub="lifetime" />
          <StatCard label="Total Benefit" value={fmt(stats.lifetime_benefit)} sub="savings" accent />
          <StatCard label="Orders Placed" value={String(stats.orders_count || 0)} sub={fmt(stats.total_paid)} />
        </div>
      )}

      {/* Slab progress card */}
      {loading ? (
        <div className="overline text-center py-6">Loading…</div>
      ) : slab?.has_slab ? (
        <>
          <SlabProgressCard slab={slab} />
          <SlabOrderPanel slab={slab} user={{ ...(salon || {}), email: user?.email || "" }} onPlaced={() => {
            // refresh slab + stats after order
            Promise.all([
              api.get("/salon/my-slab").catch(() => ({ data: { has_slab: false } })),
              api.get("/salon/my-stats").catch(() => ({ data: null })),
            ]).then(([s, st]) => {
              setSlab(s.data);
              setStats(st.data);
            });
          }} />
        </>
      ) : (
        <div className="border border-dashed border-black/15 p-7 text-center text-sm text-[#5C5C5C]" data-testid="slab-none">
          No active slab assigned. Contact your Blisson partnerships manager to enrol in our
          Silver / Gold / Platinum loyalty programmes.
        </div>
      )}

      {/* Order history */}
      {stats?.orders?.length > 0 && (
        <OrderHistory orders={stats.orders} />
      )}
    </div>
  );
}

function StatCard({ label, value, sub, accent = false }) {
  return (
    <div className={`border p-4 ${accent ? "border-[#C9A961] bg-[#F8F5F0]/40" : "border-black/10"}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="overline text-[10px]">{label}</div>
      <div className="font-serif-display text-2xl mt-1">{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-wider text-[#5C5C5C] mt-0.5">{sub}</div>}
    </div>
  );
}

function OrderHistory({ orders }) {
  return (
    <div data-testid="order-history">
      <div className="overline mb-3">Order History</div>
      <div className="border border-black/10 divide-y divide-black/5">
        {orders.map((o) => (
          <div key={o.order_id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`order-${o.order_id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-xs">{o.order_id}</div>
              <div className="text-[10px] text-[#5C5C5C] uppercase tracking-wider mt-0.5">
                {o.created_at?.slice(0, 10)} · {o.items?.length || 0} items
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-medium text-sm">₹{Number(o.total || 0).toLocaleString("en-IN")}</div>
              <div className={`text-[10px] uppercase tracking-wider mt-0.5 ${
                o.status === "paid" || o.status === "delivered" ? "text-[#C9A961]" :
                o.status === "shipped" ? "text-blue-600" : "text-[#5C5C5C]"
              }`}>{o.status}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlabProgressCard({ slab }) {
  const a = slab.assignment || {};
  const tier = (a.tier || "silver").toLowerCase();
  const tierColors = {
    silver: { bg: "from-[#E5E4E2] to-white", text: "text-[#7A7A7A]", border: "border-[#BFBFBF]" },
    gold: { bg: "from-[#F8F5F0] to-white", text: "text-[#C9A961]", border: "border-[#C9A961]" },
    platinum: { bg: "from-[#0F0F0F] to-[#1f1f1f]", text: "text-white", border: "border-black" },
  };
  const palette = tierColors[tier] || tierColors.silver;
  const isPlatinum = tier === "platinum";

  return (
    <div
      className={`border ${palette.border} bg-gradient-to-br ${palette.bg} p-8`}
      data-testid="slab-progress-card"
    >
      <div className={`flex items-center justify-between mb-5 ${isPlatinum ? "text-white" : ""}`}>
        <div>
          <div className={`overline ${palette.text}`}>★ {tier.toUpperCase()} SLAB</div>
          <h3 className="font-serif-display text-3xl tracking-tight mt-1">{a.slab_name || tier}</h3>
        </div>
        <div className="text-right">
          <div className={`text-[10px] uppercase tracking-wider opacity-70`}>Payable</div>
          <div className="font-serif-display text-2xl">₹{Number(a.payable_amount || 0).toLocaleString("en-IN")}</div>
          {a.payment_received ? (
            <div className="text-[10px] uppercase tracking-wider text-[#C9A961] mt-1">✓ Paid</div>
          ) : (
            <div className="text-[10px] uppercase tracking-wider opacity-60 mt-1">Pending payment</div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className={`flex items-center justify-between text-xs mb-2 ${isPlatinum ? "text-white" : "text-[#5C5C5C]"}`}>
          <span>MRP Consumed</span>
          <span data-testid="slab-progress-text">
            ₹{Number(slab.mrp_consumed || 0).toLocaleString("en-IN")} / ₹{Number(a.mrp_access_amount || 0).toLocaleString("en-IN")} ({slab.progress_pct || 0}%)
          </span>
        </div>
        <div className={`h-2 ${isPlatinum ? "bg-white/15" : "bg-black/10"} overflow-hidden`}>
          <div
            className="h-full bg-[#C9A961] transition-all duration-700"
            style={{ width: `${Math.min(100, slab.progress_pct || 0)}%` }}
            data-testid="slab-progress-bar"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-6">
        <div>
          <div className={`text-[10px] uppercase tracking-wider opacity-70`}>Remaining MRP</div>
          <div className="font-serif-display text-2xl" data-testid="slab-remaining-mrp">
            ₹{Number(slab.mrp_remaining || 0).toLocaleString("en-IN")}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-[10px] uppercase tracking-wider opacity-70`}>Days Remaining</div>
          <div className="font-serif-display text-2xl" data-testid="slab-days-remaining">
            {slab.days_remaining ?? "—"}
          </div>
        </div>
      </div>

      <div className={`text-[10px] uppercase tracking-wider opacity-60 mt-6 pt-4 border-t ${isPlatinum ? "border-white/15" : "border-black/10"}`}>
        Active from {a.starts_at?.slice(0, 10)} · Expires {a.expires_at?.slice(0, 10)}
      </div>
    </div>
  );
}

function Rejected({ salon, children }) {
  return (
    <div className="space-y-6" data-testid="salon-rejected">
      <div className="border border-red-200 bg-red-50/50 p-7 text-center">
        <XCircle size={28} className="text-red-600 mx-auto mb-3" strokeWidth={1.2} />
        <div className="overline mb-2 text-red-700">Application Not Approved</div>
        <p className="text-sm text-[#5C5C5C] max-w-md mx-auto">
          {salon?.admin_note || "We couldn't verify your salon. You can update the details below and re-submit."}
        </p>
      </div>
      {children}
    </div>
  );
}

function FormField({ label, value, onChange, onBlur, error, testid, placeholder = "", inputMode, maxLength }) {
  const hasError = Boolean(error);
  return (
    <label className="block">
      <div className={`overline mb-2 transition-colors ${hasError ? "text-red-600" : ""}`}>{label}</div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        aria-invalid={hasError}
        className={`w-full bg-transparent border-b outline-none py-2.5 text-sm transition-colors ${
          hasError
            ? "border-red-500 focus:border-red-600 text-red-700"
            : "border-black/15 focus:border-[#C9A961]"
        }`}
        data-testid={`salon-input-${testid}`}
      />
      {hasError && (
        <div className="flex items-start gap-1.5 mt-1.5 text-[11px] text-red-600 leading-snug" data-testid={`salon-err-${testid}`}>
          <AlertCircle size={12} strokeWidth={1.6} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </label>
  );
}


/* ---------------- Slab Order Panel ---------------- */

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const INDIAN_STATES = [
  "Gujarat", "Maharashtra", "Rajasthan", "Madhya Pradesh", "Delhi",
  "Haryana", "Uttar Pradesh", "Punjab", "Karnataka", "Tamil Nadu",
  "Telangana", "Andhra Pradesh", "Kerala", "West Bengal", "Bihar",
  "Odisha", "Assam", "Jharkhand", "Chhattisgarh", "Uttarakhand",
  "Himachal Pradesh", "Goa", "Jammu & Kashmir", "Other",
];

const absUrl = (u) => (!u ? "" : u.startsWith("http") || u.startsWith("data:") ? u : `${BACKEND_URL}${u}`);

function SlabOrderPanel({ slab, user, onPlaced }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [bag, setBag] = useState({}); // { product_id: quantity }
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState({
    full_name: user?.owner_name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    address: "",
    city: user?.city || "",
    state: "Gujarat",
    postal_code: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    const { errors: e } = validateShippingForm(form);
    setErrors(e);
  }, [form]);

  // Load products once
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => setProducts([]));
  }, []);

  // Live quote (debounced)
  useEffect(() => {
    const items = Object.entries(bag)
      .filter(([, q]) => Number(q) > 0)
      .map(([product_id, quantity]) => ({ product_id, quantity: Number(quantity) }));
    if (items.length === 0) {
      setQuote(null);
      return;
    }
    setQuoting(true);
    const t = setTimeout(() => {
      api
        .post("/salon/slab-quote", { items, state: form.state })
        .then((r) => setQuote(r.data))
        .catch((e) => {
          toast.error(e?.response?.data?.detail || "Could not compute quote");
          setQuote(null);
        })
        .finally(() => setQuoting(false));
    }, 350);
    return () => clearTimeout(t);
  }, [bag, form.state]);

  const categories = useMemo(() => {
    const cs = Array.from(new Set(products.map((p) => p.category))).sort();
    return ["All", ...cs];
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchCat = category === "All" || p.category === category;
      const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [products, category, search]);

  const addToBag = (pid) => setBag((b) => ({ ...b, [pid]: (Number(b[pid]) || 0) + 1 }));
  const removeFromBag = (pid) => setBag((b) => {
    const next = { ...b };
    const cur = Number(next[pid]) || 0;
    if (cur <= 1) delete next[pid];
    else next[pid] = cur - 1;
    return next;
  });
  const setQty = (pid, q) => setBag((b) => {
    const n = Math.max(0, Math.floor(Number(q) || 0));
    if (n === 0) {
      const next = { ...b };
      delete next[pid];
      return next;
    }
    return { ...b, [pid]: n };
  });

  const placeOrder = async () => {
    if (!quote || Object.keys(bag).length === 0) return toast.error("Add at least one product");
    const { errors: e, hasErrors } = validateShippingForm(form);
    setTouched({
      full_name: true, email: true, phone: true, address: true,
      city: true, state: true, postal_code: true,
    });
    setErrors(e);
    if (hasErrors) {
      const firstKey = ["full_name","email","phone","address","city","state","postal_code"].find((k) => e[k]);
      return toast.error(e[firstKey] || "Please fix the highlighted fields.");
    }
    setPlacing(true);
    try {
      const items = Object.entries(bag)
        .filter(([, q]) => Number(q) > 0)
        .map(([product_id, quantity]) => ({ product_id, quantity: Number(quantity) }));
      const normalized = normalizeShippingForm(form);
      const { data } = await api.post("/orders", {
        items,
        is_slab_order: true,
        ...normalized,
        payment_method: quote.total_payable > 0 ? "upi_qr" : "slab_only",
      });
      toast.success(
        quote.total_payable > 0
          ? `Order placed. Pay ${fmtINR(quote.total_payable)} via UPI to confirm.`
          : "Order placed using your slab — no extra payment needed."
      );
      setBag({});
      setQuote(null);
      onPlaced && onPlaced();
      navigate(`/order-success?id=${data.order_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  const bagCount = Object.values(bag).reduce((s, q) => s + (Number(q) || 0), 0);

  return (
    <section className="border-2 border-[#C9A961]/40 bg-gradient-to-br from-[#F8F5F0]/40 to-white p-5 md:p-7" data-testid="slab-order-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShoppingBag size={16} className="text-[#C9A961]" />
        <div className="overline text-[#C9A961]">Order using your slab</div>
      </div>
      <h3 className="font-serif-display text-2xl md:text-3xl tracking-tight mb-2">
        Build your salon order
      </h3>
      <p className="text-xs text-[#5C5C5C] mb-5 max-w-2xl leading-relaxed">
        Add products below — they're charged at <strong>MRP</strong> and consume your slab balance.
        If your bag goes <strong>over the slab limit</strong>, the extra amount is billed at
        <strong> 50% off MRP</strong> plus shipping. Within slab? Zero extra to pay.
      </p>

      {/* Search + category filter */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-black/15 focus:border-[#C9A961] outline-none"
            data-testid="slab-order-search"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="bg-white border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm"
          data-testid="slab-order-category"
        >
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Product grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[460px] overflow-y-auto pr-1" data-testid="slab-order-products">
        {filtered.length === 0 ? (
          <div className="col-span-full text-center text-xs text-[#5C5C5C] py-8 italic">No products match.</div>
        ) : (
          filtered.map((p) => {
            const qty = Number(bag[p.id]) || 0;
            const mrp = Number(p.original_price || p.price || 0);
            return (
              <div key={p.id} className="bg-white border border-black/10 p-3 flex gap-3 items-center" data-testid={`slab-product-${p.id}`}>
                <div className="w-16 h-16 bg-[#F8F5F0] shrink-0 overflow-hidden">
                  {p.image && <img src={absUrl(p.image)} alt={p.name} loading="lazy" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm leading-tight truncate" title={p.name}>{p.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5C5C] mt-0.5">{p.category}</div>
                  <div className="font-serif-display text-base mt-1">{fmtINR(mrp)} <span className="text-[10px] text-[#5C5C5C] uppercase tracking-wider">MRP</span></div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {qty === 0 ? (
                    <button
                      onClick={() => addToBag(p.id)}
                      className="px-3 py-1.5 bg-black text-white text-[10px] tracking-[0.18em] uppercase hover:bg-[#C9A961] transition-colors"
                      data-testid={`slab-add-${p.id}`}
                    >
                      Add
                    </button>
                  ) : (
                    <div className="flex items-center border border-black/15" data-testid={`slab-qty-${p.id}`}>
                      <button onClick={() => removeFromBag(p.id)} className="w-7 h-7 hover:bg-[#F8F5F0]"><Minus size={11} /></button>
                      <input
                        type="number"
                        value={qty}
                        onChange={(e) => setQty(p.id, e.target.value)}
                        className="w-9 text-center text-xs border-0 outline-none bg-transparent"
                      />
                      <button onClick={() => addToBag(p.id)} className="w-7 h-7 hover:bg-[#F8F5F0]"><Plus size={11} /></button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Quote summary */}
      {bagCount > 0 && (
        <div className="mt-5 border border-[#C9A961]/40 bg-[#F8F5F0]/60 p-4 md:p-5" data-testid="slab-quote-summary">
          <div className="overline mb-3">
            Bag summary {quoting && <Loader2 size={11} className="inline animate-spin ml-2" />}
          </div>
          {quote ? (
            <div className="text-sm space-y-1.5">
              <Line label="Items (MRP total)" value={fmtINR(quote.cart_mrp)} />
              <Line label={`Slab covers (${fmtINR(quote.slab?.mrp_remaining_before || 0)} available)`} value={`− ${fmtINR(quote.slab_consumed_this_order)}`} accent />
              {quote.overflow_mrp > 0 && (
                <>
                  <div className="pt-2 border-t border-dashed border-black/15" />
                  <Line label={`Overflow (above slab) @ MRP`} value={fmtINR(quote.overflow_mrp)} />
                  <Line label={`− ${quote.overflow_discount_pct}% discount on overflow`} value={`− ${fmtINR(quote.overflow_mrp - quote.overflow_payable)}`} accent />
                  <Line label="Shipping" value={quote.shipping > 0 ? fmtINR(quote.shipping) : "FREE"} />
                </>
              )}
              <div className="pt-2 border-t border-black/20 flex justify-between font-medium text-base" data-testid="slab-quote-total">
                <span>You pay</span>
                <span className="font-serif-display text-xl">
                  {quote.total_payable > 0 ? fmtINR(quote.total_payable) : <span className="text-[#16a34a]">₹0 — fully covered</span>}
                </span>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#5C5C5C] mt-1">
                Slab remaining after order: {fmtINR(quote.slab?.mrp_remaining_after || 0)}
              </div>
            </div>
          ) : (
            <div className="text-xs text-[#5C5C5C]">Computing…</div>
          )}
        </div>
      )}

      {/* Address form */}
      {bagCount > 0 && (
        <div className="mt-5 border border-black/10 bg-white p-4 md:p-5" data-testid="slab-address-form">
          <div className="overline mb-3">Shipping details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField
              testid="full-name"
              label="Full Name"
              value={form.full_name}
              onChange={(v) => setForm((f) => ({ ...f, full_name: v }))}
              onBlur={() => setTouched((t) => ({ ...t, full_name: true }))}
              error={touched.full_name && errors.full_name}
            />
            <FormField
              testid="phone"
              label="Phone (10-digit mobile)"
              value={form.phone}
              onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
              onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
              error={touched.phone && errors.phone}
              inputMode="tel"
              maxLength={15}
            />
            <FormField
              testid="email"
              label="Email"
              value={form.email}
              onChange={(v) => setForm((f) => ({ ...f, email: v }))}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
              error={touched.email && errors.email}
              inputMode="email"
            />
            <FormField
              testid="city"
              label="City"
              value={form.city}
              onChange={(v) => setForm((f) => ({ ...f, city: v }))}
              onBlur={() => setTouched((t) => ({ ...t, city: true }))}
              error={touched.city && errors.city}
            />
            <div className="md:col-span-2">
              <FormField
                testid="address"
                label="Address"
                value={form.address}
                onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                onBlur={() => setTouched((t) => ({ ...t, address: true }))}
                error={touched.address && errors.address}
                placeholder="Shop / Salon address"
              />
            </div>
            <label className="block">
              <span className="overline mb-2 block">State</span>
              <select
                value={form.state}
                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2.5 text-sm"
                data-testid="slab-input-state"
              >
                {INDIAN_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <FormField
              testid="postal"
              label="Postal Code (6-digit PIN)"
              value={form.postal_code}
              onChange={(v) => setForm((f) => ({ ...f, postal_code: v.replace(/\D/g, "").slice(0, 6) }))}
              onBlur={() => setTouched((t) => ({ ...t, postal_code: true }))}
              error={touched.postal_code && errors.postal_code}
              inputMode="numeric"
              maxLength={6}
            />
          </div>
        </div>
      )}

      {/* Place order */}
      {bagCount > 0 && (
        <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
          <div className="text-[11px] text-[#5C5C5C]">
            {bagCount} item{bagCount === 1 ? "" : "s"} in bag
          </div>
          <button
            onClick={placeOrder}
            disabled={placing || !quote || quoting}
            className="btn-primary disabled:opacity-60"
            data-testid="slab-place-order"
          >
            {placing ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
            {placing
              ? "Placing…"
              : quote?.total_payable > 0
                ? `Place Order · Pay ${fmtINR(quote.total_payable)}`
                : "Place Order (slab covers it)"}
          </button>
        </div>
      )}
    </section>
  );
}

function Line({ label, value, accent }) {
  return (
    <div className={`flex items-center justify-between ${accent ? "text-[#C9A961]" : ""}`}>
      <span className={accent ? "" : "text-[#5C5C5C]"}>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
